import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Loader2, Users } from "lucide-react";
import { rolesApi, Role } from "@/api/client";

export default function UserRolesSettings() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    display_order: 0,
    is_active: true,
  });

  const { data: rolesData, isLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: () => rolesApi.list(),
  });

  const roles = rolesData?.roles || [];

  const createMutation = useMutation({
    mutationKey: ["createRole"],
    mutationFn: rolesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      handleCancel();
    },
  });

  const updateMutation = useMutation({
    mutationKey: ["updateRole"],
    mutationFn: ({
      id,
      ...data
    }: { id: string } & Parameters<typeof rolesApi.update>[1]) =>
      rolesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      handleCancel();
    },
  });

  const deleteMutation = useMutation({
    mutationKey: ["deleteRole"],
    mutationFn: rolesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });

  const handleCancel = () => {
    setShowForm(false);
    setEditingRole(null);
    setFormData({
      name: "",
      slug: "",
      description: "",
      display_order: 0,
      is_active: true,
    });
  };

  const handleEdit = (role: Role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      slug: role.slug,
      description: role.description || "",
      display_order: role.display_order,
      is_active: role.is_active,
    });
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (editingRole) {
      updateMutation.mutate({
        id: editingRole.id,
        name: formData.name,
        slug: formData.slug || undefined,
        description: formData.description || undefined,
        display_order: formData.display_order,
        is_active: formData.is_active,
      });
    } else {
      createMutation.mutate({
        name: formData.name,
        slug: formData.slug || undefined,
        description: formData.description || undefined,
        display_order: formData.display_order,
      });
    }
  };

  const handleDelete = (role: Role) => {
    if (
      confirm(
        `Are you sure you want to delete the "${role.name}" role? This action cannot be undone.`,
      )
    ) {
      deleteMutation.mutate(role.id);
    }
  };

  const toggleActive = (role: Role) => {
    updateMutation.mutate({
      id: role.id,
      name: role.name,
      is_active: !role.is_active,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">User Roles</h1>
          <p className="mt-1 text-sm text-gray-500">
            Define roles for categorizing users (e.g., QA, DevOps, Engineering)
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Role
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="card mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="input"
                  placeholder="e.g., QA, DevOps, Engineering"
                  required
                />
              </div>
              <div>
                <label className="label">Slug (optional)</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) =>
                    setFormData({ ...formData, slug: e.target.value })
                  }
                  className="input"
                  placeholder="Auto-generated if empty"
                />
                <p className="text-xs text-gray-500 mt-1">
                  URL-friendly identifier
                </p>
              </div>
            </div>

            <div>
              <label className="label">Description (optional)</label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="input"
                rows={2}
                placeholder="Brief description of this role"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Display Order</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      display_order: parseInt(e.target.value) || 0,
                    })
                  }
                  className="input"
                  min={0}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lower numbers appear first
                </p>
              </div>
              {editingRole && (
                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_active: e.target.checked,
                        })
                      }
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="btn btn-primary"
              >
                {createMutation.isPending || updateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : editingRole ? (
                  "Update Role"
                ) : (
                  "Create Role"
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Roles List */}
      <div className="space-y-3">
        {roles.map((role) => (
          <div
            key={role.id}
            className={`card flex items-center justify-between ${
              !role.is_active ? "bg-gray-50" : ""
            }`}
          >
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{role.name}</span>
                  <span className="text-xs text-gray-400">({role.slug})</span>
                  {!role.is_active && (
                    <span className="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-600">
                      Inactive
                    </span>
                  )}
                </div>
                {role.description && (
                  <p className="text-sm text-gray-500">{role.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => toggleActive(role)}
                className={`px-3 py-1 text-xs rounded-full ${
                  role.is_active
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {role.is_active ? "Active" : "Inactive"}
              </button>
              <button
                onClick={() => handleEdit(role)}
                className="p-2 text-gray-400 hover:text-blue-600"
                title="Edit"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDelete(role)}
                className="p-2 text-gray-400 hover:text-red-600"
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        {roles.length === 0 && (
          <div className="card text-center py-8">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No roles defined yet</p>
            <p className="text-sm text-gray-400 mt-1">
              Create roles to categorize your users
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
